import { parseISO } from 'date-fns';

/**
 * Geometry + time helpers for the booking timeline (Gantt) view.
 *
 * The timeline is an absolute-positioned canvas: the X axis is clock time
 * (driven by a dynamic {@link HourRange}) and the Y axis is table rows. All
 * layout math lives here so it can be unit-tested without a DOM, mirroring the
 * Swing `BookingTimelineView` coordinate logic.
 */

export interface HourRange {
  startHour: number;
  endHour: number;
}

export const DEFAULT_HOUR_RANGE: HourRange = { startHour: 9, endHour: 24 };

// Layout constants (px). Kept close to the Swing values for visual parity.
export const HOUR_WIDTH = 90; // px per hour
export const ROW_HEIGHT = 48; // px per table row
export const HEADER_HEIGHT = 40; // time-axis header height
export const TABLE_LABEL_WIDTH = 150; // row-header (table name) column width
export const BAR_MARGIN = 3; // vertical inset of a bar within its row band

export const SNAP_MINUTES = 15;
export const MIN_DURATION_MINUTES = 15;
export const DEFAULT_DURATION_MINUTES = 90; // assumed when a booking has no end

export const MINUTES_PER_PIXEL = 60 / HOUR_WIDTH;

/** Minutes since local midnight for an ISO instant. */
export const localMinutesOfDay = (iso: string): number => {
  const d = parseISO(iso);
  return d.getHours() * 60 + d.getMinutes();
};

/** Total pixel width of the time axis for a range. */
export const rangeWidthPx = (range: HourRange): number =>
  (range.endHour - range.startHour) * HOUR_WIDTH;

/** Minutes-of-day -> x pixel (0 = range.startHour). */
export const minutesToX = (minutesOfDay: number, range: HourRange): number =>
  (minutesOfDay - range.startHour * 60) / MINUTES_PER_PIXEL;

/** x pixel -> minutes-of-day, snapped to the nearest {@link SNAP_MINUTES}. */
export const xToMinutes = (px: number, range: HourRange, snap: number = SNAP_MINUTES): number => {
  const raw = px * MINUTES_PER_PIXEL + range.startHour * 60;
  return Math.round(raw / snap) * snap;
};

/** Clamp a minutes-of-day value to the visible range. */
export const clampMinutes = (minutesOfDay: number, range: HourRange): number =>
  Math.max(range.startHour * 60, Math.min(range.endHour * 60, minutesOfDay));

export const tableIndexToY = (index: number): number => index * ROW_HEIGHT;

export const yToTableIndex = (y: number): number => Math.floor(y / ROW_HEIGHT);

export interface BarSpan {
  x: number;
  width: number;
  startMin: number;
  endMin: number;
}

/**
 * Pixel span of a booking within the range, or null if it falls entirely
 * outside it. A missing end defaults to {@link DEFAULT_DURATION_MINUTES}; an
 * overnight end (end < start) is clamped to the end of the display.
 */
/**
 * Map a wall-clock minutes-of-day value into the displayed window
 * [startHour*60, startHour*60 + 1440). Times before the range start (i.e. after
 * midnight in an extended-day view) are pushed to the tail of the day.
 */
export const toDisplayMinutes = (minutesOfDay: number, range: HourRange): number =>
  minutesOfDay < range.startHour * 60 ? minutesOfDay + 24 * 60 : minutesOfDay;

export const bookingSpan = (
  startIso: string,
  endIso: string | null,
  range: HourRange,
): BarSpan | null => {
  let startMin = toDisplayMinutes(localMinutesOfDay(startIso), range);
  let endMin = endIso
    ? toDisplayMinutes(localMinutesOfDay(endIso), range)
    : startMin + DEFAULT_DURATION_MINUTES;

  // Overnight booking that didn't wrap into the tail: cap at end of display.
  if (endMin <= startMin) {
    endMin = range.endHour * 60;
  }

  // Clamp to the visible window.
  startMin = Math.max(startMin, range.startHour * 60);
  endMin = Math.min(endMin, range.endHour * 60);

  if (endMin <= startMin) {
    return null; // outside the range
  }

  const x = minutesToX(startMin, range);
  const width = minutesToX(endMin, range) - x;
  return { x, width, startMin, endMin };
};

/** True when two absolute instant spans overlap (touching edges do not count). */
export const instantsOverlap = (
  aStart: string,
  aEnd: string | null,
  bStart: string,
  bEnd: string | null,
): boolean => {
  const aS = parseISO(aStart).getTime();
  const aE = aEnd ? parseISO(aEnd).getTime() : aS + DEFAULT_DURATION_MINUTES * 60_000;
  const bS = parseISO(bStart).getTime();
  const bE = bEnd ? parseISO(bEnd).getTime() : bS + DEFAULT_DURATION_MINUTES * 60_000;
  return aS < bE && bS < aE;
};

export interface ScheduleHours {
  openTime: string | null; // "HH:mm"
  closeTime: string | null; // "HH:mm"
}

export interface SpecialDayHours {
  isClosed?: boolean;
  openTime?: string | null;
  closeTime?: string | null;
}

const parseHour = (hhmm: string | null | undefined): { hour: number; minute: number } | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':');
  const hour = Number(h);
  const minute = Number(m ?? 0);
  if (!Number.isFinite(hour)) return null;
  return { hour, minute: Number.isFinite(minute) ? minute : 0 };
};

/**
 * Resolve the visible hour range for a day, mirroring Swing's rules:
 * a special day with custom hours wins; otherwise earliest-open/latest-close
 * across the day's schedules (close rounded up if it has minutes). Returns null
 * when the day is closed or has no schedule (caller shows the empty state).
 */
export const computeHourRange = (
  schedules: ScheduleHours[],
  specialDay: SpecialDayHours | null | undefined,
): HourRange | null => {
  if (specialDay) {
    if (specialDay.isClosed) return null;
    const open = parseHour(specialDay.openTime);
    const close = parseHour(specialDay.closeTime);
    if (open && close) {
      let endHour = close.hour + (close.minute > 0 ? 1 : 0);
      if (endHour <= open.hour) endHour = open.hour + 1;
      return { startHour: open.hour, endHour };
    }
    // Special day without custom hours falls through to regular schedules.
  }

  let earliestOpen = Number.POSITIVE_INFINITY;
  let latestClose = Number.NEGATIVE_INFINITY;
  for (const s of schedules) {
    const open = parseHour(s.openTime);
    const close = parseHour(s.closeTime);
    if (open) earliestOpen = Math.min(earliestOpen, open.hour);
    if (close) latestClose = Math.max(latestClose, close.hour + (close.minute > 0 ? 1 : 0));
  }

  if (!Number.isFinite(earliestOpen) || !Number.isFinite(latestClose)) {
    return null;
  }
  let endHour = latestClose;
  if (endHour <= earliestOpen) endHour = earliestOpen + 1;
  return { startHour: earliestOpen, endHour };
};

/** Group sorted-ascending contiguous integers, e.g. [0,1,2,4,5] -> [[0,1,2],[4,5]]. */
export const groupContiguous = (indices: number[]): number[][] => {
  if (indices.length === 0) return [];
  const sorted = [...indices].sort((a, b) => a - b);
  const groups: number[][] = [];
  let current: number[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1] + 1) {
      current.push(sorted[i]);
    } else {
      groups.push(current);
      current = [sorted[i]];
    }
  }
  groups.push(current);
  return groups;
};

/** Parse a CSV id list ("1,2,3") to numbers. */
export const csvToIds = (csv: string | null | undefined): number[] => {
  if (!csv) return [];
  return csv
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
};
